// index.jsx
import React from "react"
import { createRoot } from "react-dom/client"
import { HtmlEditorCommander } from "aldoo-ra/Commander"
import LinkDialog from "./link-dialog"

const { runCommand } = HtmlEditorCommander

const addLink = async (data) => {
  const { selectionRange, setSelectionRange, variables } = data
  if (!selectionRange || selectionRange.collapsed) return

  // Get the selected text
  const selectedText = selectionRange.toString()
  if (!selectedText) return

  // Create a container for the dialog
  const container = document.createElement("div")
  document.body.appendChild(container)

  // Create promise to handle dialog result
  return new Promise((resolve) => {
    const root = createRoot(container)
    root.render(
      <LinkDialog
        variables={variables}
        onClose={(result) => {
          root.unmount()
          container.remove()
          resolve(result)
        }}
      />
    )
  }).then((result) => {
    if (!result) return // Dialog cancelled

    const { url, isDownload, openInSameTab } = result

    // Create link element
    const link = document.createElement("a")

    // Check if URL is a variable ({{...}}) and handle it specially
    const isVariable = /^\{\{.*\}\}$/.test(url.trim())
    if (isVariable) {
      // For variables, set href attribute directly to avoid URL encoding
      link.setAttribute("href", url)
    } else {
      link.href = url
    }
    link.textContent = selectedText
    link.className =
      "editor-link text-admin_primary hover:text-admin_primary_dk underline hover:no-underline"
    // Add download attribute
    if (isDownload) link.setAttribute("download", "")
    // Open in same tab
    link.setAttribute("target", openInSameTab ? "_self" : "_blank")

    // Insert the link
    const range = selectionRange.cloneRange()
    range.deleteContents()
    range.insertNode(link)
    range.setStartAfter(link)
    range.setEndAfter(link)

    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    setSelectionRange(range)

    return link
  })
}

const editLink = (data) => {
  const { target, variables } = data
  if (!target || target.tagName !== "A") return

  // Remove any existing menu first
  const existingMenu = document.querySelector(".link-menu-container")
  if (existingMenu) {
    existingMenu.remove()
  }

  // Open the edit dialog directly instead of the preview menu
  const container = document.createElement("div")
  document.body.appendChild(container)

  const root = createRoot(container)
  root.render(
    <LinkDialog
      defaultUrl={target.getAttribute("href") || target.href}
      defaultIsDownload={target.hasAttribute("download")}
      defaultOpenInSameTab={target.target === "_self"}
      variables={variables}
      linkElement={target} // Pass the link element for additional actions
      onClose={(result) => {
        if (result) {
          runCommand("link.update", {
            target: target,
            url: result.url,
            isDownload: result.isDownload,
            openInSameTab: result.openInSameTab,
          })
        }
        root.unmount()
        container.remove()
      }}
    />
  )

  // Return cleanup function
  return () => {
    root.unmount()
    container.remove()
  }
}

const deleteLink = (data) => {
  const { target } = data
  if (!target || target.tagName !== "A") return

  const text = target.textContent
  const textNode = document.createTextNode(text)
  target.parentNode.replaceChild(textNode, target)
}

const updateLink = (data) => {
  const { target, url, isDownload, openInSameTab } = data
  if (!target || target.tagName !== "A") return

  // Check if URL is a variable ({{...}}) and handle it specially
  const isVariable = /^\{\{.*\}\}$/.test(url.trim())

  if (isVariable) {
    // For variables, set href attribute directly to avoid URL encoding
    // Note: target.href will still show the encoded version when accessed as a property,
    // but target.getAttribute("href") and the actual HTML will contain the raw variable
    target.setAttribute("href", url)
  } else {
    target.href = url
  }

  //set the download attribute
  if (isDownload) {
    target.setAttribute("download", "")
  } else {
    target.removeAttribute("download")
  }

  //set the target attribute
  target.setAttribute("target", openInSameTab ? "_self" : "_blank")
}

const Link = ({ action, data }) => {
  switch (action) {
    case "add":
      return addLink(data)
    case "edit":
      return editLink(data)
    case "delete":
      return deleteLink(data)
    case "update":
      return updateLink(data)
    default:
      console.warn(`Unknown link action: ${action}`)
      return null
  }
}

export default Link
